/**
 * 카드 입력 클라이언트 검증 유틸 (#430 — #1413 follow-up).
 *
 * 결제 시 카드 입력(BloominCardInputForm)과 저장 카드 등록(AddCardModal /
 * bloominRegistration — Bloomin crearTarjeta 경로)이 공유하는 1차 가드.
 *
 * ## 권위 경계 (불변)
 * - 본 유틸은 제출 *전* UX 레이어 — 서버(BE 토큰화 `/api/tarjetas`)가 최종 권위.
 * - fail-open 금지: 검증 실패 시 제출 차단. 단, 서버 거절 흐름은 기존 그대로.
 * - brand 검출은 **표시용 + CVV 길이 보조** — payload 계약(`marca` 등) 미변경.
 */

/** PAN prefix 로 검출 가능한 카드 브랜드 (표시용) */
export type CardBrand = 'visa' | 'mastercard' | 'amex' | 'unknown';

/**
 * Luhn 체크섬 검증 (mod 10).
 *
 * 호출 전 숫자만 남긴 PAN 을 전달해야 한다 (빈 문자열은 false).
 */
export function isValidLuhn(value: string): boolean {
  if (!/^\d+$/.test(value)) {
    return false;
  }

  let sum = 0;
  let alternate = false;

  for (let i = value.length - 1; i >= 0; i -= 1) {
    let digit = Number(value[i]);
    if (alternate) {
      digit *= 2;
      if (digit > 9) digit -= 9;
    }
    sum += digit;
    alternate = !alternate;
  }

  return sum % 10 === 0;
}

/**
 * PAN prefix 기반 brand 검출 (표시용).
 *
 * - visa: 4
 * - mastercard: 51~55 또는 2221~2720 (2-시리즈)
 * - amex: 34 / 37
 * - 그 외(또는 아직 판별 불가한 부분 입력): unknown
 *
 * 부분 입력 중에는 판별 가능한 시점부터만 brand 를 반환한다
 * (예: '5' 단독 → unknown, '51' → mastercard, '222' → unknown, '2221' → mastercard).
 */
export function detectCardBrand(pan: string): CardBrand {
  const digits = pan.replace(/\D/g, '');

  if (digits.startsWith('4')) {
    return 'visa';
  }

  if (/^5[1-5]/.test(digits)) {
    return 'mastercard';
  }

  // Mastercard 2-시리즈 — 첫 4자리 2221~2720
  if (digits.length >= 4) {
    const prefix4 = Number(digits.slice(0, 4));
    if (prefix4 >= 2221 && prefix4 <= 2720) {
      return 'mastercard';
    }
  }

  if (/^3[47]/.test(digits)) {
    return 'amex';
  }

  return 'unknown';
}

/** brand 표시용 라벨 (고유명사 — i18n 불필요) */
export const CARD_BRAND_LABELS: Record<Exclude<CardBrand, 'unknown'>, string> = {
  visa: 'VISA',
  mastercard: 'Mastercard',
  amex: 'AMEX',
};

/**
 * brand 별 허용 CVV 길이.
 *
 * - amex: 4자리 (CID)
 * - visa / mastercard: 3자리
 * - unknown: 3~4자리 허용 (brand 미판별 시 길이만으로 차단하지 않음 — 서버 권위)
 */
export function allowedCvvLengths(brand: CardBrand): readonly number[] {
  switch (brand) {
    case 'amex':
      return [4];
    case 'visa':
    case 'mastercard':
      return [3];
    default:
      return [3, 4];
  }
}

/** CVV 검증 — 숫자 + brand 별 정확 길이 */
export function isValidCvv(cvv: string, brand: CardBrand): boolean {
  return /^\d+$/.test(cvv) && allowedCvvLengths(brand).includes(cvv.length);
}

/**
 * 만료일 미래(당월 포함) 검증.
 *
 * 카드 만료일은 해당 월 말일까지 유효 — 당월 입력은 통과한다.
 * 월 단위 비교만 수행하므로 멕시코 TZ 등 시간대 영향이 없다
 * (월 경계 수 시간 오차는 서버 권위 검증이 흡수).
 *
 * @param month 1~12
 * @param year 4자리 연도
 * @param now 기준 시각 (테스트 주입용, 기본 현재)
 */
export function isExpiryInFuture(month: number, year: number, now: Date = new Date()): boolean {
  if (!Number.isInteger(month) || month < 1 || month > 12 || !Number.isInteger(year)) {
    return false;
  }

  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth() + 1;

  return year > currentYear || (year === currentYear && month >= currentMonth);
}
